import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { NumberRangeBaseFunction, NumberRangeDescription, NumberRangeSubFunction } from "../../shared/NumberRange";
import { config } from "../../config";
import { useEventsApi } from "../../hooks/useEventsApi";
import ApiErrorDialog, { type ApiErrorDialogContent } from "../../components/ApiErrorDialog";
import { describeApiError } from "../../utils/apiErrorMessage";
import EventResultPage from "../EventResultPage";
import { normalizeTranslationReviewMessage } from "../../constants/translationReview";
import CreatorConclusionSummary from "../../components/CreatorConclusionSummary";
import { normalizeEventLanguage } from "../../constants/languages";

interface Props {
  data: any; // enthält alle Steps: step1, step2, step3a
  eventCreation: string;
  onValidationChange: (valid: boolean) => void;
  risingStepValid: boolean;
  resolvingStepValid: boolean;
  /** Fired once when the event was created and the result page is shown — wizard should not allow going back to resubmit. */
  onFlowCompleted?: () => void;
  /** Quick action from success screen to start a fresh creator flow. */
  onCreateAnotherEvent?: () => void;
}

export default function Step4Form({
  data,
  eventCreation,
  onValidationChange,
  risingStepValid,
  resolvingStepValid,
  onFlowCompleted,
  onCreateAnotherEvent,
}: Props) {
  useEffect(() => {
    fetchBaseFunctions();
    fetchSubFunctions();
  }, []);

  const step1 = data.step1;
  const step2 = data.step2;
  const step3a = data.step3a;
  const step3b = data.step3b;

  const { callApi } = useEventsApi();
  const [loading, setLoading] = useState(false);
  const [errorDialog, setErrorDialog] = useState<ApiErrorDialogContent | null>(null);
  const [loadingBase, setLoadingBase] = useState(true);
  const [baseFunctions, setBaseFunctions] = useState<NumberRangeBaseFunction[]>([]);
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [subFunctions, setSubFunctions] = useState<NumberRangeSubFunction[]>([]);

  const [eventRisingSuccessfullyCreated, setRisingEventSuccessfullyCreated] = useState(false);
  const [eventResolvingSuccessfullyCreated, setResolvingEventSuccessfullyCreated] = useState(false);

  const [receivedRisingEventCode, setReceivedRisingEventCode] = useState(0);
  const [receivedRisingEventId, setReceivedRisingEventId] = useState(0);
  const [receivedResolvingEventCode, setReceivedResolvingEventCode] = useState(0);
  const [receivedResolvingEventId, setReceivedResolvingEventId] = useState(0);
  const [risingTranslationStatus, setRisingTranslationStatus] = useState<any>({ status: "idle" });
  const [resolvingTranslationStatus, setResolvingTranslationStatus] = useState<any>({ status: "idle" });
  const [risingTranslationReview, setRisingTranslationReview] = useState<{ message: string } | null>(
    null
  );
  const [resolvingTranslationReview, setResolvingTranslationReview] = useState<{ message: string } | null>(
    null
  );

  const finishPrerequisitesMet =
    (eventCreation === "rising" && risingStepValid) ||
    (eventCreation === "resolving" && resolvingStepValid) ||
    (eventCreation === "both" && risingStepValid && resolvingStepValid);

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  useEffect(() => {
    onValidationChangeRef.current(finishPrerequisitesMet);
  }, [finishPrerequisitesMet]);

  const hasMeaningfulDescriptionContent = (desc: any): boolean => {
    const hasText = ["label", "label_long", "description", "situation"].some((k) =>
      String(desc?.[k] ?? "").trim() !== ""
    );
    const hasActions =
      Array.isArray(desc?.actions) && desc.actions.some((a: any) => String(a ?? "").trim() !== "");
    return hasText || hasActions;
  };

  const sanitizeDescriptionsForCreator = (descriptions: any[], primaryLang: string) => {
    const primary = normalizeEventLanguage(primaryLang) || "ger";
    return (descriptions ?? []).filter((desc) => {
      const lang = normalizeEventLanguage(desc?.language_code);
      if (lang === primary) return true;
      return hasMeaningfulDescriptionContent(desc);
    });
  };


  const handleSubmit = async () => {
    try {
      setLoading(true);
      let risingPosted = false;
      let resolvingPosted = false;

      //Rising
      let payloadRising = "";
      let responseRising;
      if (eventCreation === "rising" || eventCreation === "both") {
        const risingPrimary =
          normalizeEventLanguage(step3a?.attributes?.creator_source_language) || "ger";
        payloadRising = {
          ...step3a,
          attributes: {
            ...step3a?.attributes,
            alarm: false,
            message_history: false,
          },
          event_descriptions: sanitizeDescriptionsForCreator(
            step3a?.event_descriptions ?? [],
            risingPrimary
          ),
          version: step3a.version ?? 0,
          auto_eventcode: true,
          creator_mode: true,
          base_id: step1?.selectedBaseFunctionId,
          sub_id: step2?.selectedSubFunction,
        };
        responseRising = await callApi(`${config.apiUrl}/api/events/`, "POST", payloadRising);
        setReceivedRisingEventCode(responseRising.event_code);
        setReceivedRisingEventId(responseRising.global_event_id);
        if (responseRising?.translation_review_required) {
          setRisingTranslationReview({
            message: normalizeTranslationReviewMessage(responseRising.translation_review_message),
          });
        } else {
          setRisingTranslationReview(null);
        }
        setRisingEventSuccessfullyCreated(true);
        risingPosted = true;
      }

      //Resolving
      let payloadResolving = "";
      let responseResolving;
      if (eventCreation === "resolving" || eventCreation === "both") {
        if (eventCreation === "both") {
          step3b.event_code = 10000 + Number(responseRising.event_code);
        }
        // resolving-only: step3b.event_code is already set from rising pair (10256 = 256 + 10000)
        const resolvingPrimary =
          normalizeEventLanguage(step3b?.attributes?.creator_source_language) || "ger";
        const resolvingAttrs = {
          ...(step3b.attributes ?? {}),
          alarm: false,
          message_history: false,
        };
        delete (resolvingAttrs as Record<string, unknown>).creator_resolving_rising_hmi_labels;
        delete (resolvingAttrs as Record<string, unknown>).creator_resolving_rising_contract_name;
        payloadResolving = {
          ...step3b,
          ...(eventCreation === "both"
            ? {
                event_type: responseRising?.event_type ?? step3a?.event_type ?? step3b?.event_type,
                module: responseRising?.module ?? step3a?.module ?? step3b?.module,
              }
            : {}),
          attributes: resolvingAttrs,
          event_descriptions: sanitizeDescriptionsForCreator(
            step3b?.event_descriptions ?? [],
            resolvingPrimary
          ),
          version: step3b.version ?? 0,
          auto_eventcode: false,
          creator_mode: true,
          base_id: step1?.selectedBaseFunctionId,
          sub_id: step2?.selectedSubFunction,
        };
        responseResolving = await callApi(`${config.apiUrl}/api/events/`, "POST", payloadResolving);
        setReceivedResolvingEventCode(responseResolving.event_code);
        setReceivedResolvingEventId(responseResolving.global_event_id);
        if (responseResolving?.translation_review_required) {
          setResolvingTranslationReview({
            message: normalizeTranslationReviewMessage(responseResolving.translation_review_message),
          });
        } else {
          setResolvingTranslationReview(null);
        }
        setResolvingEventSuccessfullyCreated(true);
        resolvingPosted = true;
      }

      const creationSucceeded =
        (eventCreation === "rising" && risingPosted) ||
        (eventCreation === "resolving" && resolvingPosted) ||
        (eventCreation === "both" && risingPosted && resolvingPosted);

      if (creationSucceeded) {
        // Same tick as successful POSTs so parent clears UnsavedChanges before paint/navigation (RR + navbar).
        onFlowCompleted?.();
      }
    } catch (error) {
      console.error(error);
      const { messages, status } = describeApiError(error, "Error while creating event.");
      setErrorDialog({
        title: "Creating the event failed",
        summary: "The event was not created. The server reported:",
        messages,
        status,
      });
    } finally {
      setLoading(false);
    }
  };


  const fetchBaseFunctions = async () => {
    try {
      setLoadingBase(true);
      const url = `${config.apiUrl}/api/number-range-base-function/?page_size=all`;
      const res = await callApi(url, "GET");
      const raw = Array.isArray(res?.results) ? res.results : Array.isArray(res) ? res : [];
      setBaseFunctions(raw as NumberRangeBaseFunction[]);
    } catch (error) {
      console.error("Fehler beim Laden der BaseFunctions:", error);
    } finally {
      setLoadingBase(false);
    }
  };

  const fetchSubFunctions = async () => {
    try {
      setLoadingSubs(true);
      const url = `${config.apiUrl}/api/number-range-sub-function/?base=${step1?.selectedBaseFunctionId}&page_size=all`;
      const res = await callApi(url, "GET");
      const raw = Array.isArray(res?.results) ? res.results : Array.isArray(res) ? res : [];
      setSubFunctions(raw as NumberRangeSubFunction[]);
    } catch (error) {
      console.error("Fehler beim Laden der SubFunctions:", error);
    } finally {
      setLoadingSubs(false);
    }
  };

  const getLabel = (id: number) => {
    const bf = baseFunctions.find((b) => b.id === id);
    if (!bf) return "...";

    const desc = bf.descriptions?.find(d => d.language_code === "eng");
    return desc?.display_name ?? `Funktion ${bf.digit}`;
  };

  const getSubLabel = (sf: NumberRangeSubFunction) => {
    const desc = sf.descriptions?.find((d: NumberRangeDescription) => d.language_code === "eng");
    return desc?.display_name ?? `SubFunction ${sf.id}`;
  };

  useEffect(() => {
    if (!receivedRisingEventId) return;
    let active = true;
    let intervalId: number | undefined;

    const stop = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const poll = async () => {
      try {
        const status = await callApi(
          `${config.apiUrl}/api/events/${receivedRisingEventId}/translation-status/`,
          "GET"
        );
        if (!active) return;
        setRisingTranslationStatus(status || { status: "idle" });
        const st = String(status?.status ?? "").toLowerCase();
        if (st === "done" || st === "error") {
          stop();
        }
      } catch {
        if (active) setRisingTranslationStatus({ status: "error", message: "Status request failed." });
        stop();
      }
    };

    void poll();
    intervalId = window.setInterval(poll, 3000);
    return () => {
      active = false;
      stop();
    };
  }, [receivedRisingEventId, callApi]);

  useEffect(() => {
    if (!receivedResolvingEventId) return;
    let active = true;
    let intervalId: number | undefined;

    const stop = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const poll = async () => {
      try {
        const status = await callApi(
          `${config.apiUrl}/api/events/${receivedResolvingEventId}/translation-status/`,
          "GET"
        );
        if (!active) return;
        setResolvingTranslationStatus(status || { status: "idle" });
        const st = String(status?.status ?? "").toLowerCase();
        if (st === "done" || st === "error") {
          stop();
        }
      } catch {
        if (active) setResolvingTranslationStatus({ status: "error", message: "Status request failed." });
        stop();
      }
    };

    void poll();
    intervalId = window.setInterval(poll, 3000);
    return () => {
      active = false;
      stop();
    };
  }, [receivedResolvingEventId, callApi]);


  // --------------------------------
  // Loading Spinner
  // --------------------------------
  if (loadingBase || loadingSubs) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "40vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const selectedBase = baseFunctions.find((b) => b.id === step1?.selectedBaseFunctionId);
  const selectedSub = subFunctions.find((sf) => sf.id === step2?.selectedSubFunction);

  if ((!eventRisingSuccessfullyCreated && (eventCreation === "rising" || eventCreation === "both"))
        || (!eventResolvingSuccessfullyCreated && (eventCreation === "resolving" || eventCreation === "both"))) {
    const baseLabel = step1?.selectedBaseFunctionId
      ? getLabel(step1.selectedBaseFunctionId)
      : "—";
    const subLabel = selectedSub ? getSubLabel(selectedSub) : "—";

    return (
      <>
        <CreatorConclusionSummary
          eventCreation={eventCreation}
          baseLabel={baseLabel}
          subLabel={subLabel}
          baseDigit={selectedBase?.digit}
          subDigit={selectedSub?.digit}
          showRisingDetails={eventCreation === "rising" || eventCreation === "both"}
          showResolvingDetails={eventCreation === "resolving" || eventCreation === "both"}
          risingSeverity={step3a?.attributes?.severity}
          risingDescriptions={step3a?.event_descriptions}
          risingPrimaryLanguage={step3a?.attributes?.creator_source_language}
          risingContractName={step3a?.event_contract_name ?? null}
          resolvingSeverity={step3b?.attributes?.severity}
          resolvingDescriptions={step3b?.event_descriptions}
          resolvingPrimaryLanguage={step3b?.attributes?.creator_source_language}
          resolvingContractName={step3b?.event_contract_name ?? null}
          loading={loading}
          canFinish={finishPrerequisitesMet}
          onFinish={() => void handleSubmit()}
        />
        <ApiErrorDialog content={errorDialog} onClose={() => setErrorDialog(null)} />
      </>
    );
  } else {
    return (
      <Box p={2}>
        <EventResultPage
          fullEventRising={step3a}
          risingEventCode={receivedRisingEventCode}
          risingEventId={receivedRisingEventId}
          fullEventResolving={step3b}
          resolvingEventCode={receivedResolvingEventCode}
          resolvingEventId={receivedResolvingEventId}
          risingTranslationStatus={risingTranslationStatus}
          resolvingTranslationStatus={resolvingTranslationStatus}
          risingTranslationReview={risingTranslationReview}
          resolvingTranslationReview={resolvingTranslationReview}
          onCreateAnotherEvent={onCreateAnotherEvent}
        />
      </Box>
    )
  }
}